export const MY_WORK_TABS = [
  { id: "overdue", label: "Overdue" },
  { id: "dueToday", label: "Due Today" },
  { id: "inProgress", label: "In Progress" },
  { id: "completed", label: "Completed" },
];

export const PAGE_SIZE = 10;

export const mapHomeTaskRow = (task) => ({
  id: task.taskId,
  projectTitleId: task.projectTitleId,
  taskName: task.taskName,
  projectName: task.projectName,
  projectId: task.projectId,
  taskTypeName: task.taskTypeName,
  estimateHours: task.estimateHours,
  priorityId: task.priorityId,
  priorityName: task.priorityName,
  dueDate: task.dueDate,
  taskStatusId: task.taskStatusId,
  statusName: task.statusName,
});

export const formatDueDate = (dueDate) => {
  if (!dueDate) return { dateLabel: "—", hint: null, tone: "neutral" };

  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) return { dateLabel: "—", hint: null, tone: "neutral" };

  const dateLabel = due.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDay = new Date(due);
  dueDay.setHours(0, 0, 0, 0);

  const diffDays = Math.round((dueDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    const days = Math.abs(diffDays);
    return {
      dateLabel,
      hint: days === 1 ? "1 day overdue" : `${days} days overdue`,
      tone: "overdue",
    };
  }
  if (diffDays === 0) return { dateLabel, hint: "Due today", tone: "today" };
  if (diffDays === 1) return { dateLabel, hint: "Due tomorrow", tone: "soon" };
  if (diffDays <= 7) return { dateLabel, hint: `Due in ${diffDays} days`, tone: "soon" };
  return { dateLabel, hint: null, tone: "neutral" };
};

export const filterTasks = (tasks, filters) => {
  const search = (filters.search || "").trim().toLowerCase();

  return (tasks || []).filter((task) => {
    if (search) {
      const haystack = `${task.projectTitleId || ""} ${task.taskName || ""} ${task.projectName || ""}`.toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    if (filters.project && String(task.projectId) !== String(filters.project)) return false;
    if (filters.priority && String(task.priorityName || "").toLowerCase() !== String(filters.priority).toLowerCase()) {
      return false;
    }
    if (filters.status && String(task.statusName || "").toLowerCase() !== String(filters.status).toLowerCase()) {
      return false;
    }
    return true;
  });
};

/** Unique by id (for Project). */
export const uniqueOptionsById = (tasks, idKey, labelKey) => {
  const map = new Map();
  (tasks || []).forEach((task) => {
    const id = task[idKey];
    const label = task[labelKey];
    if (id == null || !label) return;
    if (!map.has(String(id))) {
      map.set(String(id), { value: String(id), label });
    }
  });
  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
};

/** Unique by label (for Priority — same name can have different ids across projects). */
export const uniqueOptionsByLabel = (tasks, labelKey) => {
  const map = new Map();
  (tasks || []).forEach((task) => {
    const label = task[labelKey];
    if (!label) return;
    const key = String(label).toLowerCase();
    if (!map.has(key)) {
      map.set(key, { value: String(label), label: String(label) });
    }
  });
  return sortPriorityOptions(Array.from(map.values()));
};

/** Project DDL options from assigned projects (stable across My Work tabs). */
export const projectOptionsFromAssigned = (projects) => {
  const map = new Map();
  (projects || []).forEach((project) => {
    const id = project.projectId;
    const label = project.projectName;
    if (id == null || !label) return;
    if (!map.has(String(id))) {
      map.set(String(id), { value: String(id), label: String(label) });
    }
  });
  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
};

const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };

const sortPriorityOptions = (options) =>
  [...(options || [])].sort((a, b) => {
    const ao = PRIORITY_ORDER[String(a.label || "").toLowerCase()];
    const bo = PRIORITY_ORDER[String(b.label || "").toLowerCase()];
    if (ao != null && bo != null) return ao - bo;
    if (ao != null) return -1;
    if (bo != null) return 1;
    return String(a.label || "").localeCompare(String(b.label || ""));
  });

/** Priority DDL options from master priority list (stable across My Work tabs). */
export const priorityOptionsFromMaster = (priorities) => {
  const map = new Map();
  (priorities || []).forEach((priority) => {
    const label = priority.priorityName || priority.label;
    if (!label) return;
    const key = String(label).toLowerCase();
    if (!map.has(key)) {
      map.set(key, { value: String(label), label: String(label) });
    }
  });
  return sortPriorityOptions(Array.from(map.values()));
};

/** Flatten all My Work tab lists (fallback when master lists are empty). */
export const flattenMyWorkLists = (listsByTab) =>
  MY_WORK_TABS.flatMap((tab) => listsByTab?.[tab.id] || []);

export const getVisiblePages = (currentPage, totalPages, windowSize = 5) => {
  if (totalPages <= 0) return [];
  let start = Math.max(currentPage - Math.floor(windowSize / 2), 1);
  let end = Math.min(start + windowSize - 1, totalPages);
  if (end - start < windowSize - 1) {
    start = Math.max(end - windowSize + 1, 1);
  }
  const pages = [];
  for (let i = start; i <= end; i += 1) pages.push(i);
  return pages;
};
